import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { concatMap, map, takeUntil } from 'rxjs/operators';
import { BasePayrollComponent } from '../../../../components/base/base-payroll.component';
import {
  MasterPayrollObject,
  PayrollApprovalTotals,
  PayrollConfigSetupData,
  PayrollStartData,
  PayrollStatuses,
  PayTypes,
  TaxHierarchy
} from '../../../../models/payroll';
import { PayrollService } from '../../../../services/payroll.service';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { AuditService } from '../../../../services/audit.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WarningLevel } from '../../../../models/error-detail';
import { FlattenResponseMessagesByTypes } from '../../../../utils/api-response-helper';
import { IPayrollCorrectionsPage } from '../../../../guards/can-deactivate-correction-payroll.guard';
import { ClientSelectionState } from '../../../../state/ClientSelectionState/ClientSelection.state';
import { Store } from '@ngxs/store';
import { forkJoin, Subject } from 'rxjs';
import { PayrollInfo } from '../../../../models/payroll-info';
import {
  PayrollSummary,
  PayrollSummaryOrderedLists,
  PayrollSummaryService,
  TaxCodeAndType
} from '../../../../services/payroll-summary.service';
import { CompaniesService } from '../../../../services/companies.service';
import { AdminService } from '../../../../services/admin.service';
import { CompanyLocalTaxConfig, CompanyTaxCodeConfig } from '../../../../models/tax-config';
import { Division } from '../../../../models/division';

@Component({
  selector: 'lib-corrections-payroll-preview',
  templateUrl: './payroll-corrections-preview.component.html',
  styleUrls: ['./payroll-corrections-preview.component.css'],
  standalone: false
})
export class PayrollCorrectionsPreviewComponent
  extends BasePayrollComponent
  implements OnInit, IPayrollCorrectionsPage
{
  clientCode: string;
  destroy$ = new Subject<void>();
  payroll: MasterPayrollObject;
  payrollInfo: PayrollInfo;
  startData: PayrollStartData;
  configSetupData: PayrollConfigSetupData;

  // summary modal
  clientName: string;
  approveTotals: Record<string, number> = {};
  responseMessages: any[];
  payrollApprovalTotals: PayrollApprovalTotals;
  isPayrollApproved: boolean = true;

  public taxConfigs: CompanyTaxCodeConfig[] = [];
  public localTaxConfigs: CompanyLocalTaxConfig[] = [];
  public masterTaxMap: TaxCodeAndType[] = [];
  public taxHierarchies: TaxHierarchy[] = [];
  public divisions: Division[] = [];

  public payrollSummary: PayrollSummary;
  public orderedLists: PayrollSummaryOrderedLists;
  public hasRecentHistoryVoids = false;

  constructor(
    public payrollMgmt: PayrollManagement,
    private payrollSvc: PayrollService,
    private companyService: CompaniesService,
    private adminService: AdminService,
    private auditSvc: AuditService,
    private ngModalService: NgbModal,
    private payrollSummaryService: PayrollSummaryService,
    private router: Router,
    private store: Store
  ) {
    super();
  }

  ngOnInit(): void {
    this.payrollMgmt
      .getAdjustmentPayroll(this.clientCode, this.year, this.seqNo)
      .pipe(
        concatMap(p => {
          this.payroll = p;

          // error condition - payroll type doesn't match up.
          if (this.payroll.PayType != PayTypes.Adjustment) {
            this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
          }

          return this.payrollSvc
            .getPayrollConfigSetupData(
              p.ClientCode,
              p.Calendars,
              p.LocationId,
              p.PayDate,
              p.PayType
            )
            .pipe(
              map(setupData => {
                this.configSetupData = setupData;
                this.negateCorrectedEarnings(this.payroll);
              })
            );
        }),
        concatMap(() => {
          return this.store.select(ClientSelectionState.client).pipe(
            takeUntil(this.destroy$),
            map(x => {
              if (x != null) {
                this.clientName = x.DBA || x.Name;
              }
            })
          );
        }),
        concatMap(() => {
          return this.payrollSvc
            .getPayrollStartData(this.clientCode)
            .pipe(map(data => (this.startData = data)));
        }),
        concatMap(() => {
          return this.payrollSvc
            .getPayrollInfo(this.clientCode, this.year, this.seqNo)
            .pipe(map(data => (this.payrollInfo = data)));
        }),
        concatMap(() => {
          return this.companyService
            .getTaxConfigs(this.clientCode, true)
            .pipe(map(data => (this.taxConfigs = data)));
        }),
        concatMap(() => {
          return this.companyService
            .getLocalTaxConfigs(this.clientCode, null, null)
            .pipe(map(data => (this.localTaxConfigs = data)));
        }),
        concatMap(() => {
          return this.adminService
            .getTaxHierarchy()
            .pipe(map(data => (this.taxHierarchies = data)));
        }),
        concatMap(() => {
          return this.companyService
            .getDivisions(this.clientCode)
            .pipe(map(resp => (this.divisions = resp)));
        })
      )
      .subscribe(() => {
        this.payrollSummary = this.payrollSummaryService.CalculatePayrollTotals(
          this.payroll,
          this.configSetupData
        );

        this.masterTaxMap = this.payrollSummaryService.BuildMasterTaxMap(
          this.taxHierarchies,
          this.taxConfigs,
          this.localTaxConfigs
        );

        this.orderedLists = this.payrollSummaryService.GenerateOrderedLists(
          this.payrollSummary,
          this.configSetupData,
          this.masterTaxMap
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  negateCorrectedEarnings(payroll: MasterPayrollObject) {
    for (let emp of payroll.EmployeePays) {
      for (let payline of emp.Paylines) {
        if (payline.OkToPay) {
          payline.Earnings.forEach(e => {
            e.CurrentAmount > 0 ? (e.CurrentAmount *= -1) : e;
            e.Hours > 0 ? (e.Hours *= -1) : e;
          });

          payline.Taxes.forEach(t => (t.CurrentAmount > 0 ? (t.CurrentAmount *= -1) : t));
          payline.Deductions.forEach(d => (d.CurrentAmount > 0 ? (d.CurrentAmount *= -1) : d));
        }
      }
    }
  }

  public showHistoryVoidMessage(value: boolean) {
    this.hasRecentHistoryVoids = value;
  }

  submitApproval(content) {
    this.payrollSvc
      .submitApproval(
        this.clientCode,
        this.year,
        this.seqNo,
        this.payrollInfo.SuppressNotifications
      )
      .subscribe(resp => {
        this.payroll.Status = PayrollStatuses.Approved;
        this.payrollApprovalTotals = resp.Model;
        this.responseMessages = FlattenResponseMessagesByTypes(
          [WarningLevel.Critical, WarningLevel.Informational, WarningLevel.Warning],
          resp.Messages
        );

        if (!resp.Model) {
          this.isPayrollApproved = false;
        } else {
          this.auditSvc.auditPayroll(
            'payroll-corrections-complete',
            {
              ClientCode: this.clientCode,
              LocationId: this.payroll.LocationId,
              PayDate: this.payroll.PayDate,
              PayrollType: this.payroll.PayType,
              SequenceNumber: this.payroll.SequenceNumber,
              Calendars: this.payroll.Calendars
            },
            {
              EmployeePays: JSON.stringify(this.payroll.EmployeePays)
            }
          );
        }
        this._showSubmitApprovalModal(content);
      });
  }

  returnToHub() {
    this.router.navigate([`/client/${this.clientCode}/payroll-corrections`]);
  }

  private _showSubmitApprovalModal(content) {
    this._calculateApprovalTotals();
    this.ngModalService.open(content, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
  }

  public closeModal() {
    // if user clicked the close of the modal, we need to route them properly
    this.ngModalService.dismissAll();

    if (this.isPayrollApproved) {
      this.router.navigate([`/client/${this.clientCode}`]);
    } else {
      this.router.navigate([`/client/${this.clientCode}/payroll-corrections`]);
    }
  }

  public print() {
    window.print();
  }

  private _calculateApprovalTotals() {
    // debit totals
    this.approveTotals['DebitTotal'] =
      this.payrollApprovalTotals.BillingDebit +
      this.payrollApprovalTotals.PayrollDebit +
      this.payrollApprovalTotals.TaxFilingDebit +
      this.payrollApprovalTotals.ThirdPartyDisbursementAch;

    // check totals
    this.approveTotals['CheckTotal'] =
      this.payrollApprovalTotals.NetChecks +
      this.payrollApprovalTotals.ThirdPartyDisbursementChecks;
  }
}
